/**
 * FILE-OVERVIEW:
 * - 役割: メモ画面の「メール取り込み」タブ。一覧・本文・PDFプレビューを出す。
 * - 主な連携: services/mailApi.ts, Memo/index.tsx
 * - 本番運用上の注意: 表示するのは社外から届いたメール。
 *   **本文HTMLを innerHTML で描画しないこと。** スクリプトを仕込まれる。
 *   プレーンテキストだけを出し、HTMLは別タブで開かせる。
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton,
  Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  fetchMailDetail, fetchMailList, formatReceivedAt, setMailRead,
} from '../../services/mailApi';
import type { MailDetail, MailListRow } from '../../services/mailApi';

interface MailTabProps {
  isMobile: boolean;
  /** 未読件数が変わったときに親へ知らせる。タブのバッジに使う */
  onUnreadChange?: () => void;
}

const PAGE_SIZE = 30;

/** バイト数を読める形にする */
const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const MailTab = ({ isMobile, onUnreadChange }: MailTabProps) => {
  const [rows, setRows] = useState<MailListRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<MailDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  /** 一覧を先頭から読み直す */
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMailList(PAGE_SIZE);
      setRows(res.items);
      setCursor(res.cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'メール一覧を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** 続きを読む */
  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchMailList(PAGE_SIZE, cursor);
      setRows((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : '続きを取得できませんでした');
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * 行を開く。開いただけでは既読にしない。
   * 「読んだ」は本人が詳細の「読みました」を押して記録する
   */
  const openMail = async (row: MailListRow) => {
    setDetailLoading(true);
    setDetailError(null);
    setSelected(null);
    try {
      const detail = await fetchMailDetail(row.mailId);
      setSelected(detail);
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : 'メールを取得できませんでした');
    } finally {
      setDetailLoading(false);
    }
  };

  /** 一覧と開いている詳細の両方に既読状態を反映する */
  const applyRead = (mailId: string, isRead: boolean) => {
    setRows((prev) => prev.map((r) => (r.mailId === mailId ? { ...r, isRead } : r)));
    setSelected((prev) => (prev?.mailId === mailId ? { ...prev, isRead } : prev));
  };

  /** 既読・未読を切り替える。失敗したら見た目を元に戻す。成功したように見せない */
  const changeRead = async (mailId: string, next: boolean, onFail: (message: string) => void) => {
    applyRead(mailId, next);
    try {
      await setMailRead(mailId, next);
      onUnreadChange?.();
    } catch {
      applyRead(mailId, !next);
      onFail('既読の切り替えに失敗しました');
    }
  };

  /** 一覧から既読・未読を切り替える（開かずに処理を終えたい場合） */
  const toggleRead = (row: MailListRow, event: React.MouseEvent) => {
    // 行クリック（詳細を開く）に伝播させない
    event.stopPropagation();
    void changeRead(row.mailId, !row.isRead, setError);
  };

  const listPane = (
    <Box sx={{ flex: selected || detailLoading ? '0 0 42%' : 1, minWidth: 0, overflow: 'auto' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1,
      }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {loading ? '読み込み中…' : `${rows.length}件`}
        </Typography>
        <Tooltip title="最新を取得">
          <span>
            <IconButton size="small" onClick={() => void reload()} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Box sx={{ px: 2, py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">取り込まれたメールはまだありません</Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 44 }} />
                <TableCell sx={{ whiteSpace: 'nowrap' }}>受信</TableCell>
                {!isMobile && <TableCell>差出人</TableCell>}
                <TableCell>件名</TableCell>
                {!isMobile && <TableCell sx={{ whiteSpace: 'nowrap' }}>添付</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.mailId}
                  hover
                  onClick={() => void openMail(row)}
                  selected={selected?.mailId === row.mailId}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ pr: 0 }}>
                    {/* 未読は「色」だけで示さない。太字と記号を併用する */}
                    <Tooltip title={row.isRead ? '未読に戻す' : '既読にする'}>
                      <IconButton size="small" onClick={(e) => void toggleRead(row, e)}>
                        {row.isRead
                          ? <MarkEmailReadIcon fontSize="small" color="disabled" />
                          : <MarkEmailUnreadIcon fontSize="small" color="error" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: row.isRead ? 400 : 700,
                  }}
                  >
                    {formatReceivedAt(row.receivedAt)}
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ fontWeight: row.isRead ? 400 : 700, maxWidth: 200 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 'inherit' }}>
                        {row.from}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: row.isRead ? 400 : 700 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                      {row.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {row.preview}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.attachmentCount > 0 && (
                        <Chip
                          size="small"
                          icon={<PictureAsPdfIcon />}
                          label={row.attachmentCount}
                          variant="outlined"
                        />
                      )}
                      {row.rejectedAttachmentCount > 0 && (
                        <Tooltip title="PDF以外の添付があります（保存していません）">
                          <WarningAmberIcon fontSize="small" color="warning" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {cursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? '読み込み中…' : 'さらに読み込む'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  const detailPane = (
    <Box sx={{
      flex: 1, minWidth: 0, borderLeft: isMobile ? 0 : 1, borderColor: 'divider',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1,
      }}
      >
        <Typography variant="subtitle2" color="text.secondary">本文</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {selected && (
            selected.isRead ? (
              <Tooltip title="未読に戻す">
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<CheckCircleIcon color="success" />}
                  onClick={() => void changeRead(selected.mailId, false, setDetailError)}
                  sx={{ color: 'text.secondary' }}
                >
                  既読
                </Button>
              </Tooltip>
            ) : (
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={() => void changeRead(selected.mailId, true, setDetailError)}
              >
                読みました
              </Button>
            )
          )}
          <IconButton size="small" onClick={() => { setSelected(null); setDetailError(null); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Divider />

      {detailLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}
      {detailError && <Alert severity="error" sx={{ m: 2 }}>{detailError}</Alert>}

      {selected && (
        <Box sx={{ overflow: 'auto', px: 2, pb: 3 }}>
          <Typography variant="h6" sx={{ mt: 1 }}>{selected.subject}</Typography>
          <Stack spacing={0.5} sx={{ mt: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              差出人: {selected.from}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              受信: {formatReceivedAt(selected.receivedAt)}
            </Typography>
          </Stack>

          {selected.rejectedAttachments.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              PDF以外の添付は保存していません:{' '}
              {selected.rejectedAttachments.map((r) => r.fileName).join('、')}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            {/* 社外から届いた本文。HTMLとして描画せず、必ずテキストとして出す */}
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', m: 0 }}
            >
              {selected.bodyText || '(本文なし)'}
            </Typography>
          </Paper>

          {selected.hasHtmlBody && selected.bodyHtmlUrl && (
            <Typography variant="caption" component="div" sx={{ mb: 2 }}>
              {/* 画面内に埋め込むとスクリプトが同一オリジンで動く。別タブで開かせる */}
              <Link href={selected.bodyHtmlUrl} target="_blank" rel="noopener noreferrer">
                HTML版の本文を別タブで開く
              </Link>
            </Typography>
          )}

          {selected.attachments.map((a) => (
            <Box key={a.s3Key} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PictureAsPdfIcon fontSize="small" color="error" />
                <Typography variant="body2" sx={{ flex: 1 }}>{a.fileName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatSize(a.size)}
                </Typography>
                <Link href={a.url} target="_blank" rel="noopener noreferrer" variant="caption">
                  別タブで開く
                </Link>
              </Box>
              {/* 署名付きURLをそのままブラウザ内蔵のPDFビューアに渡す */}
              <Box
                component="iframe"
                title={a.fileName}
                src={a.url}
                sx={{
                  width: '100%', height: isMobile ? 360 : 520,
                  border: 1, borderColor: 'divider', borderRadius: 1,
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  // スマホでは詳細を開いている間、一覧を隠して全画面に使う
  if (isMobile && (selected || detailLoading)) {
    return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{detailPane}</Box>;
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {listPane}
      {(selected || detailLoading || detailError) && !isMobile && detailPane}
    </Box>
  );
};

export default MailTab;
